import {
  IconChevronRight,
  IconDownload,
  IconPlus,
  IconDotsVertical,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import ListView from "../../components/common/ListView";
import NormalSearch from "../../components/common/NormalSearch";
// import SelectInput from "../../components/common/SelectInput";
import InputField from "../../components/common/InputField";
import CommonModal from "../../components/common/CommonModal";
import AddShipmentIdWise from "./_components/AddShipmentIdWise";
import { usePermission } from "../../hooks/usePermissionTabs";
import { useState, useMemo } from "react";
import {
  useDRSDownloadMutation,
  useDRSShipmentUpdateByAWBMutation,
  useDRSShipmentUpdateMutation,
  useFetchDRSDetailsData,
  useDRSShipmentRevertStatusMutation,
} from "../../apis/drsApi";
import SelectInput from "../../components/Select";
import { useWarehouseListData } from "../../apis/warehouseApi";
import Input from "../../components/Input";
import { useFetchWarehouseRoutesData } from "../../apis/routeApis";
import NewCommonModal from "../../components/common/NewCommonModal";
import toast from "react-hot-toast";
import { isSameDay } from "../../utilities/helper";
import useDebounce from "../../utilities/useDebounce";
import { fi } from "zod/v4/locales";

const formatStatus = (status: string) => {
  return status
    ?.toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export default function ShipmentList() {
  const { allowed: canReadDrs, isLoading: isPermissionLoading } = usePermission("drs", "read");
  const { allowed: canUpdateDrs } = usePermission("drs", "update");
  const [openFlet, setOpenFlet] = useState<boolean>(false);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [mode, setMode] = useState("automatic"); // Line 45: State declarations
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const openFletToggleModal = () => {
    setOpenFlet(!openFlet);
  };

  const { drsId } = useParams();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText.trim().toLowerCase(), 400);
  const downloadMutation = useDRSDownloadMutation();

  const { data, refetch } = useFetchDRSDetailsData(drsId ?? "", "DL");
  console.log(data, "data..");

  const { mutate: updateDRS, isPending: isUpdateRequestPending } =
    useDRSShipmentUpdateMutation(data?.drs?.[0]?.id);
  const { mutate: updateDRSShipmentByAWB, isPending: isByAWBRequestPending } =
    useDRSShipmentUpdateByAWBMutation(data?.drs?.[0]?.id);
  const { mutate: revertStatus, isPending: isRevertStatusPending } =
    useDRSShipmentRevertStatusMutation();

  const { data: routes } = useFetchWarehouseRoutesData(
    data?.drs?.[0]?.warehouseId ?? "",
  );

  // Search Functionality
  const filteredShipments = useMemo(() => {
    if (!data?.drs?.[0]?.shipments) return [];

    if (!debouncedSearch) return data.drs[0].shipments;

    return data.drs[0].shipments.filter((shipment: any) => {
      const awb = shipment.awbNumber?.toLowerCase() || "";
      const receiverName = shipment.receiverName?.toLowerCase() || "";
      const receiverCity = shipment.receiverCity?.toLowerCase() || "";
      const receiverDistrict = shipment.receiverDistrict?.toLowerCase() || "";

      return (
        awb.includes(debouncedSearch) ||
        receiverName.includes(debouncedSearch) ||
        receiverCity.includes(debouncedSearch) ||
        receiverDistrict.includes(debouncedSearch)
      );
    });
  }, [data?.drs?.[0]?.shipments, debouncedSearch]);

  const handleDownload = () => {
    downloadMutation.mutate(drsId ?? "");
  };

  const handleAddShipment = () => {
    if (!canUpdateDrs) {
      toast.error("You do not have permission to update DRS");
      return;
    }
    const payload = {
      shipments: selectedOrders,
    };
    updateDRS(payload, {
      onSuccess: (data: any) => {
        setOpenFlet(false);
        setSelectedOrders([]);
        refetch();
      },
      onError: (error: any) => {
        console.error(error);
        // toast.error(error?.response?.data?.message || "Something went wrong");
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          (typeof error === "string" ? error : null) ||
          "Failed to Add Shipment.";

        toast.error(errorMessage);
      },
    });
  };

  const handleAddDRSShipmentByAWB = (data: any) => {
    if (!canUpdateDrs) {
      toast.error("You do not have permission to update DRS");
      return;
    }
    const payload = {
      shipments: selectedOrders,
    };

    updateDRSShipmentByAWB(payload, {
      onSuccess: (res: any) => {
        setOpenFlet(false);
        setSelectedOrders([]);
        refetch();

        if (res?.data?.invalidOrders?.length > 0) {
          console.warn("Invalid Orders:", res.data.invalidOrders);

          // Show toast with count
          toast.error(
            `${res.data.invalidOrders
              ?.map((order: any) => order?.awb)
              ?.join(
                ",",
              )} shipments could not be assigned due to route mismatch`,
          );
        }

        toast.success(res?.message || "DRS Created Successfully");
      },
      onError: (error: any) => {
        console.error(error);
        // toast.error(error?.response?.data?.message || "Something went wrong");
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          (typeof error === "string" ? error : null) ||
          "Failed to Add Shipment.";

        toast.error(errorMessage);
      },
    });
  };

  const handleRevertStatus = (shipmentId: string, shipmentStatus: string) => {
    if (!canUpdateDrs) {
      toast.error("You do not have permission to revert status");
      return;
    }
    // Validation: Check if shipmentId is provided
    if (!shipmentId || shipmentId.trim() === "") {
      toast.error("Invalid shipment ID");
      return;
    }

    // Validation: Cannot revert DELIVERED or NOT_DELIVERED orders
    if (shipmentStatus === "DELIVERED" || shipmentStatus === "NOT_DELIVERED") {
      toast.error(`Cannot revert ${shipmentStatus.replace(/_/g, " ")} orders`);
      return;
    }

    // Validation: Check if drsId exists
    if (!data?.drs?.[0]?.id) {
      toast.error("DRS information is not available");
      return;
    }

    // Validation: Check if shipment exists in DRS
    const shipmentExists = data?.drs?.[0]?.shipments?.some(
      (shipment: any) => shipment.id === shipmentId || shipment === shipmentId,
    );

    if (!shipmentExists) {
      toast.error("This shipment does not belong to this DRS");
      return;
    }

    setOpenMenuId(null);

    const payload = {
      drsId: data?.drs?.[0]?.id,
      shipments: [shipmentId],
    };

    revertStatus(payload, {
      onSuccess: (res: any) => {
        refetch();
        toast.success(res?.message || "Status reverted successfully");
      },
      onError: (error: any) => {
        console.error(error);
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          (typeof error === "string" ? error : null) ||
          "Failed to revert status.";

        toast.error(errorMessage);
      },
    });
  };

  if (isPermissionLoading) {
    return (
      <div className="px-6 space-y-4">
        <div className="bg-white rounded-xl p-6 h-64 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canReadDrs) {
    return (
      <div className="px-6 space-y-4">
        <div className="bg-white rounded-xl p-6 text-center text-gray-500">
          No access to this section.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-6 space-y-4 mb-10">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 border border-[#000000]/10 rounded-full px-4 pr-2 py-1">
            <h2
              onClick={() => navigate(-1)}
              className="text-[#131327]/70 text-sm font-medium cursor-pointer"
            >
              DRS Management
            </h2>
            <IconChevronRight strokeWidth={1.5} size={22} />
            <h2 className="text-[#000000] text-sm font-medium">{drsId}</h2>
            <button className="flex justify-center items-center rounded-full px-4 py-2 text-[#FFFFFF] text-sm font-medium bg-[#20BF55]">
              {data?.drs?.[0]?.status}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <h2 className="text-[#212121] text-sm font-medium">
              COD Amount Collected
            </h2>
            <button className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] flex items-center gap-1 rounded-full p-2 px-4 cursor-pointer text-sm font-medium">
              <img src="/img/riyal-icon.svg" alt="" className="w-3" />
              {data?.drs?.[0]?.collectedAmount}
            </button>
          </div>
        </div>

        {/* Sections */}

        <div className="">
          <div className="flex items-center">
            <div className=" bg-[#F7F6F2] flex items-center justify-between px-6 py-3 w-full rounded-tl-3xl rounded-tr-[40px] ">
              <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
                DRS Shipments List({data?.drs?.[0]?.shipments?.length || 0})
              </h1>
              <div className="flex items-center gap-2">
                <NormalSearch
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="relative ml-5">
              <div className=" flex items-center gap-2 justify-end rounded-full">
                <button
                  onClick={handleDownload}
                  disabled={downloadMutation.isPending}
                  className={`flex items-center gap-1 p-2.5 text-sm rounded-full border border-[#FFFFFF] bg-[#F7F6F2] 
                    ${
                      downloadMutation.isPending
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                >
                  {downloadMutation.isPending ? (
                    <span className="animate-spin h-[24px] w-[24px] border-2 border-gray-400 border-t-transparent rounded-full" />
                  ) : (
                    <IconDownload stroke={1.5} size={24} />
                  )}
                </button>

                {data?.drs?.[0]?.createdAt &&
                  isSameDay(data.drs[0].createdAt) &&
                  canUpdateDrs && (
                    <button
                      onClick={openFletToggleModal}
                      className="flex items-center gap-1 p-2.5 text-sm rounded-full border border-[#FFFFFF] bg-[#F7F6F2]"
                    >
                      <IconPlus stroke={1.5} size={24} />
                    </button>
                  )}
              </div>
            </div>
          </div>
          <div className="bg-[#F7F6F2] rounded-tl-none  rounded-3xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 w-full px-4 pb-3 rounded-3xl">
              <Input
                name="warehouseId"
                type="text"
                title="Warehouse"
                placeholder=""
                disabled
                value={data?.drs?.[0]?.warehouseName}
                className="col-span-1"
              />

              <Input
                name="drsId"
                type="text"
                title="DRS ID"
                placeholder=""
                disabled
                value={data?.drs?.[0]?.drsId}
                className="col-span-1"
              />

              <Input
                name="routeId"
                type="text"
                title="Route Code"
                disabled
                placeholder=""
                value={data?.drs?.[0]?.routeCode}
                className="col-span-1"
              />

              <Input
                name="driver"
                type="text"
                title="Driver"
                disabled
                placeholder=""
                value={data?.drs?.[0]?.driver}
                className="col-span-1"
              />

              <Input
                name="date"
                type="date"
                title="Date"
                disabled
                placeholder=""
                value={
                  data?.drs?.[0]?.createdAt
                    ? data?.drs?.[0]?.createdAt?.split("T")[0]
                    : ""
                }
                className="col-span-1"
              />
            </div>

            <ListView
              data={filteredShipments}
              columns={[
                { label: "#", render: (_: any, i: number) => i + 1 },
                {
                  label: "AWB No.",
                  render: (s: any) => (
                    <div className="flex flex-col items-center gap-1 py-1">
                      <span className="font-bold text-[#212121]">{s.awbNumber}</span>
                      {s.isSwapped && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border leading-none bg-emerald-100 text-emerald-800 border-emerald-300">
                          SWAPPED
                        </span>
                      )}
                    </div>
                  ),
                },
                { label: "Origin", render: (c: any) => c.senderCity },
                {
                  label: "Destination", // 👈 main header
                  children: [
                    { label: "Country", render: (c: any) => c.receiverCountry },
                    { label: "City", render: (c: any) => c.receiverCity },
                    {
                      label: "District",
                      render: (c: any) => c.receiverDistrict,
                    },
                  ],
                },
                {
                  label: "Order Type",
                  children: [
                    { label: "Type", render: (c: any) => c.paymentType },
                    { label: "Amount", render: (c: any) => c.codAmount },
                  ],
                },
                {
                  label: "Status",
                  render: (c: any) => {
                    let statusClasses = "";
                    switch (c.status) {
                      case "DELIVERED":
                        statusClasses = "bg-lime-100 text-lime-800";
                        break;
                      case "OUT_FOR_DELIVERY":
                        statusClasses = "bg-[#F7E9CE] text-[#212121]";
                        break;
                      case "DELETED":
                        statusClasses = "bg-red-200 text-[#212121]";
                        break;
                      default:
                        statusClasses = "bg-gray-200 text-[#212121]";
                    }
                    return (
                      <button
                        className={`px-3 py-1 rounded-full ${statusClasses}`}
                      >
                        {formatStatus(c.status)}
                      </button>
                    );
                  },
                },
                {
                  label: "Action",
                  render: (c: any) => {
                    // Don't show action button for DELIVERED or NOT_DELIVERED orders
                    if (
                      c.status === "DELIVERED" ||
                      c.status === "NOT_DELIVERED"
                    ) {
                      return null;
                    }

                    return (
                      <div className="relative text-center">
                        <button
                          onClick={() =>
                            setOpenMenuId(openMenuId === c.id ? null : c.id)
                          }
                          className="p-2 border rounded-full bg-[#F7F6F2]"
                        >
                          <IconDotsVertical size={22} />
                        </button>
                        {openMenuId === c.id && (
                          <div className="absolute bg-[#F7F6F2] divide-y divide-[#979797]/50 right-full mr-2 top-1/2 transform -translate-y-1/2 z-50 text-left rounded-xl shadow-lg overflow-hidden">
                            <button
                              onClick={() => handleRevertStatus(c.id, c.status)}
                              disabled={isRevertStatusPending || !canUpdateDrs}
                              className={`block w-full px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer ${!canUpdateDrs ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              {isRevertStatusPending
                                ? "Reverting..."
                                : "Revert Status"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  },
                },
              ]}
            />
          </div>
        </div>
      </div>
      <NewCommonModal
        classWidth="w-9/12"
        title={`DRS ID - ${drsId}`}
        open={openFlet}
        closeButtonText={"Cancel"}
        onClose={openFletToggleModal}
        actionButtonText="Add Shipment"
        actionButtonOnClick={
          mode === "scan" ? handleAddDRSShipmentByAWB : handleAddShipment
        }
        isPending={isByAWBRequestPending || isUpdateRequestPending}
      >
        <AddShipmentIdWise
          routeId={data?.drs?.[0]?.routeCode}
          warehouseId={data?.drs?.[0]?.warehouseId}
          drsId={data?.drs?.[0]?.id}
          mode={mode}
          setMode={setMode}
          setSelectedOrders={setSelectedOrders}
          selectedOrders={selectedOrders}
        />
      </NewCommonModal>
    </>
  );
}
